import dicomParser from "dicom-parser";
import axiosInstance from "../../interceptor/axiosInstance";
import ApiRoutes from "../../routes/routeConstants/apiRoutes";

const SERIES_DESCRIPTION_TAG = "x0008103e";

/**
 * Fetches the first DICOM file of a series and extracts the Series Description
 * from the DICOM header (tag 0008,103E). No backend/PACS changes required.
 *
 * @param fetchDicomFiles - Function that returns the API response with { data: Array<{ file: string }> }
 * @param caseDetailId - Case ID
 * @param folder - Series folder (e.g. series UID or path)
 * @returns Series description or undefined if extraction fails
 */
export async function getSeriesDescriptionFromDicom(
  fetchDicomFiles: (caseDetailId: number, folder: string) => Promise<{ data: any[] }>,
  caseDetailId: number,
  folder: string
): Promise<string | undefined> {
  try {
    const response = await fetchDicomFiles(caseDetailId, folder);
    const data = response.data;

    let fileUrl: string | undefined;
    if (Array.isArray(data)) {
      const firstFile = data.find((item: any) => item?.file);
      fileUrl = firstFile?.file;
    } else if (data && typeof data === "object") {
      const firstValue = Object.values(data)[0];
      const firstUrl = Array.isArray(firstValue) ? firstValue[0] : firstValue;
      fileUrl = typeof firstUrl === "string" ? firstUrl : undefined;
    }
    if (!fileUrl) return undefined;

    fileUrl = fileUrl.startsWith("wadouri:") ? fileUrl.slice(8) : fileUrl;
    if (!fileUrl.startsWith("http")) {
      const base = ApiRoutes.BASE_URL || "";
      fileUrl = `${base}${fileUrl.startsWith("/") ? "" : "/"}${fileUrl}`;
    }

    let arrayBuffer: ArrayBuffer;
    const baseUrl = ApiRoutes.BASE_URL || "";
    const isExternalUrl = fileUrl.startsWith("http") && (!baseUrl || !fileUrl.startsWith(baseUrl));
    if (isExternalUrl) {
      const res = await fetch(fileUrl);
      if (!res.ok) return undefined;
      arrayBuffer = await res.arrayBuffer();
    } else {
      const res = await axiosInstance.get(fileUrl, { responseType: "arraybuffer" });
      arrayBuffer = res.data;
    }
    const byteArray = new Uint8Array(arrayBuffer);
    const dataSet = dicomParser.parseDicom(byteArray);
    const seriesDescription = dataSet.string(SERIES_DESCRIPTION_TAG);
    return seriesDescription?.trim() || undefined;
  } catch {
    return undefined;
  }
}
